/**
 * Combat context `c` — the *world/enemy* assumptions an appraisal is made under.
 *
 * The whole point of the report is that a skill's value is context-dependent:
 * anti-heal is worthless vs a non-healer, AoE is weak vs a lone boss, a stun is
 * near-free vs an already-CC'd target. So every context-sensitive quantity
 * (hit chance, E[N], heal rate, CC susceptibility, activation probabilities)
 * comes from here, never baked into an effect's price.
 *
 * `BOOTSTRAP_CONTEXT` reproduces the report's worked-example assumptions. Real
 * games replace these with telemetry / simulation output.
 */

import type { DisplacedBasicSource } from './calibration/displaced-basic.js';
import { resolveDisplacedBasicSource } from './generation/numeric/cadence-authority.js';
import type { Predicate, ResourceKind } from './types.js';
import { CANONICAL_GENERATION_EVALUATION_ANCHOR } from './reference/generation-evaluation-anchor.js';
import { CC_PRICING_SUSCEPTIBILITY, DISPLACEMENT_PRICING_SUSCEPTIBILITY } from './sim/cc-resistance.js';
import type { ReferenceProgressionBand } from './reference/types.js';

export interface ResourcePoolContext {
  /** Resource available at appraisal start. */
  start: number;
  /** Hard pool cap; omitted when the game has no known cap. */
  capacity?: number;
  regenPerS: number;
  /** Amount the build refuses to spend except in emergencies. */
  reserve: number;
}

export interface SkillReferenceValue {
  vUse: number;
  cooldownS: number;
  charges?: number;
  chargeRechargeS?: number;
}

/** A planar velocity in metres per second, as an evaluation assumption. */
export interface EvalVelocityMps {
  readonly x: number;
  readonly y: number;
}

/**
 * One weighted branch of a velocity distribution.
 *
 * Weights are relative and normalised by the reader; they do not have to sum
 * to one, but they must be finite and non-negative and at least one must be
 * positive.
 */
export interface EvalVelocityBranch {
  readonly velocityMps: EvalVelocityMps;
  readonly weight: number;
}

/**
 * How much is known about the encounter's motion, as a discriminant rather
 * than as an optional speed field.
 *
 * An optional `enemySpeedMps?: number` would have re-created the very defect
 * it was meant to fix: absent would still have to mean something, and the
 * only cheap something is zero. A discriminant makes "we do not know" a
 * state a reader must handle, so it cannot be defaulted into "still".
 *
 * - `STATIONARY` — an explicit guarantee that neither enemies nor the caster
 *   move for the shape's whole lifetime. The state the closed-form dwell
 *   model is exact for.
 * - `CONSTANT_VELOCITY` — every enemy drifts with one known velocity, and
 *   the caster with another. Priced by relative straight-line motion.
 * - `DISTRIBUTION` — enemy velocity is drawn independently per enemy from a
 *   known finite mixture. Priced as that mixture, which is exact because
 *   enemies in a Poisson field are independent and hits are counted per
 *   enemy.
 * - `UNKNOWN` — no motion information. DEFERRED.
 *
 * Deliberately NOT expressible here: chase, wander and charge, the three
 * things `src/sim/archetypes.ts` actually gives monsters. None of them is a
 * straight line, none is a fixed mixture over the shape's lifetime, and no
 * measured model for them exists. A caller cannot spell them here, so no
 * valuation can quietly claim to cover them.
 */
export type EncounterMotionEvidence =
  | { readonly kind: 'STATIONARY' }
  | {
      readonly kind: 'CONSTANT_VELOCITY';
      readonly enemyVelocityMps: EvalVelocityMps;
      readonly casterVelocityMps: EvalVelocityMps;
    }
  | {
      readonly kind: 'DISTRIBUTION';
      readonly enemyVelocityMixture: readonly EvalVelocityBranch[];
      readonly casterVelocityMps: EvalVelocityMps;
    }
  | { readonly kind: 'UNKNOWN' };

export const ENCOUNTER_MOTION_KINDS = ['STATIONARY', 'CONSTANT_VELOCITY', 'DISTRIBUTION', 'UNKNOWN'] as const;

/**
 * Where the other enemies stand when a cast fires (`eval/cast-field.ts`).
 *
 * MEASURED in the live field (the smoke harness: hunters of reactive and
 * proactive camps, auto-cast slots, per-cast positions of every enemy within
 * 20 m at the start of the cast's tick), PROVISIONAL until the live field
 * changes: camp layout, monster speed and the hunting policy all move these.
 */
export interface CastFieldEvidence {
  /** Share of casts that fire the moment the nearest enemy comes into the skill's reach. */
  readonly reachEdgeShare: number;
  /** Share of casts that fire once the hunter has closed to its engage distance; the rest fire with the nearest enemy anywhere in reach, evenly. */
  readonly engageShare: number;
  /** The distance a hunter engages from (m): a skill that reaches further still fires from here. */
  readonly engageRangeM: number;
  /** How far inside a distance the cast fires (m): enemies close in during a tick. */
  readonly firstOpportunityMarginM: number;
  /** Density of the other enemies on the line through the nearest one, on its far side, as a multiple of `EvalContext.enemyDensity`. */
  readonly bandDensityOverField: number;
  /** The density falls as `exp(-|lateral distance| / bandHalfWidthM)` from that line. */
  readonly bandHalfWidthM: number;
  /** Between the empty disc and the nearest enemy the band thins as `exp(-distance in front / frontLengthM)`. */
  readonly frontLengthM: number;
}

export interface EvalContext {
  /**
   * Which progression band's reference enemy SVP is denominated against.
   * `BalanceProfile.hpRef` is band-indexed because a boss's EHP is
   * (see `calibration/skill-damage-v13.ts`'s `V13_BOSS_EHP_Q_REF`), so this
   * selects the denominator every HP-valued effect is priced in
   * (`eval/effects.ts`'s `u_HP = 100 / hpRef[band]`).
   *
   * The default is the generation evaluation anchor's own band
   * (`reference/generation-evaluation-anchor.ts`) — the standard reference
   * point the post-numeric budget re-check is anchored to. An explicit
   * context may name a different band; that is an evaluation input, not a
   * band derived from live owner state.
   */
  referenceBandId: ReferenceProgressionBand;

  /** enemy spatial intensity ρ (enemies / m²) for AoE E[N] of deliveries the cast field does not describe (weapon contacts). */
  enemyDensity: number;

  /** Where the other enemies stand when a projectile or an area fires. */
  castField: CastFieldEvidence;

  /**
   * Representative enemy hitbox radius (m) used to dilate a delivery's region
   * when computing E[N], because an enemy is a disc and every runtime hit test
   * admits one whose BODY reaches the geometry (`src/sim/runtime.ts`
   * `enemiesInRadius`, the cone range test, `src/sim/tick-scheduler.ts`). See
   * `eval/targeting.ts`'s size-correction note.
   *
   * Optional: absent falls back to `DEFAULT_ENEMY_HITBOX_RADIUS_M`
   * (`src/sim/archetypes.ts`), which every current archetype uses — none
   * overrides `hitboxRadiusM`. Set it to model a context whose enemies are
   * bigger or smaller than that default.
   */
  enemyHitboxRadiusM?: number;

  /**
   * One server tick, in seconds, for evaluations that are resolved per tick.
   *
   * Optional, and adding it changes no existing valuation: only a shape
   * whose runtime decides something once per tick reads it. An orbit is
   * the first — the runtime resolves at most one contact per orb per
   * target per tick, so a valuation that assumed continuous time would
   * price hits the runtime will not deliver.
   *
   * Absent means such a shape is DEFERRED rather than valued on a guess.
   */
  tickS?: number;

  /**
   * What is known about how the encounter MOVES — the enemies in it, and the
   * caster whose position a follow-anchored shape is tied to.
   *
   * Separate from every other field here because motion is the one encounter
   * property this module previously had no way to express, and a shape that
   * needs it was therefore reading silence as "nothing moves". A travelling
   * shape valued against stationary enemies when the enemies in fact walk is
   * priced against an encounter that does not exist
   * (`docs/archive/gameplay/experiments/2026-09-20-orbit-encounter-valuation.md`
   * measured +21.8 % on exactly that substitution).
   *
   * Absent means `UNKNOWN`, and `UNKNOWN` is DEFERRED, never `STATIONARY`:
   * "nobody told us" and "we are guaranteed nothing moves" are different
   * facts and only one of them is a licence to price.
   *
   * Optional, and adding it changes no existing valuation: only a shape
   * whose value depends on where a target is over time reads it. Every
   * region-shaped delivery prices the same with it present or absent.
   */
  encounterMotion?: EncounterMotionEvidence;

  /** Exact stationary projectile control field, in metres from the caster, aimed along +x. Overrides density for projectile contacts. */
  projectileTargets?: readonly { readonly x: number; readonly y: number }[];

  /**
   * The spatial resolution a per-tick travelling shape is resolved at, as
   * the ratified technical profile sets it
   * (`TechnicalConstraintProfile.orbit`, `src/constraints/technical.ts`).
   *
   * Here for the same reason `tickS` is: it is a fact about the simulation
   * the valuation is predicting, and a valuation that resolved contact more
   * finely than the runtime would price contacts the runtime never makes.
   * Copied from the profile by the caller rather than read from a global,
   * so a context cannot silently disagree with the profile the runtime runs
   * under.
   *
   * Only needed where a closed form is unavailable — a still encounter is
   * priced exactly and needs no resolution at all. Absent therefore defers
   * only the cases that actually depend on it.
   */
  orbitPrecision?: {
    readonly contactToleranceM: number;
    readonly maxSegmentsPerTick: number;
  };

  /** average CC susceptibility χ of enemies in this context (0..1). */
  ccSusceptibility: number;
  displacementSusceptibility: number;

  /** fraction of a heal that lands on missing HP (rest is overheal). */
  healUtilization: number;
  /** fraction of a shield actually absorbed before it expires. */
  shieldUtilization: number;
  /** expected heal rate of anti-heal targets (HP/s). 0 vs non-healers. */
  expectedHealRate: number;

  /** DPS made available to a damage_amp over its window. */
  relevantDps: number;
  /** incoming DPS a damage_reduction / shield mitigates. */
  incomingDps: number;

  /**
   * The basic attack a cast's action lock displaces: its hit, cycle and the recovery a skill may start in. The lock costs
   * the basic hits it displaces (`displacedBasicHits`), each worth what a damage leaf of that hit is. Default: the BALANCED
   * family reference at the canonical anchor level; a cast of another source states its own (`EvaluationSourceState.displacedBasic`).
   */
  displacedBasic: DisplacedBasicSource;
  /** Expected extra combat loss per fully movement-locked second. */
  castExposureValuePerLockedS: number;

  /** expected positional-potential change produced by one useful metre. */
  positionalPotentialPerM: number;

  /** Expected removable debuff count and realized value per removed debuff. */
  expectedHarmfulStatuses: number;
  harmfulStatusValue: number;

  /** Expected fraction of a target cooldown remaining when reset/refund fires. */
  expectedCooldownRemainingFraction: number;
  fallbackReferencedSkillValue: number;
  fallbackReferencedCooldownS: number;
  /** Loadout-derived target values for cross-skill cooldown effects. */
  skillReferenceValues?: Record<string, SkillReferenceValue>;

  /** caster's current HP fraction (for HP-cost shadow price). */
  selfHpFraction: number;

  /** P(predicate) lookups by tag / generic. */
  conditionProb: (p: Predicate) => number;
  /** P(trigger fires) by event name. */
  triggerProb: Record<string, number>;

  /** starting resources & regen for the N_R availability bound (optional). */
  resources?: Partial<Record<ResourceKind, ResourcePoolContext>>;

  /** resource demand from the rest of the loadout, excluding the skill appraised. */
  resourceDemandPerS?: Partial<Record<ResourceKind, number>>;

  /** realized resource generation from the rest of the loadout. */
  resourceGenerationPerS?: Partial<Record<ResourceKind, number>>;
}

const DEFAULT_CONDITION_PROB: Record<string, number> = {
  'tag:burn': 0.45,
};
/** PROVISIONAL: P(a `hasTag` predicate holds) for a tag `DEFAULT_CONDITION_PROB` does not list. */
const UNLISTED_TAG_CONDITION_PROB_PROVISIONAL = 0.4;
/** PROVISIONAL: P(a predicate with no recognised clause holds). */
const UNRECOGNISED_PREDICATE_CONDITION_PROB_PROVISIONAL = 0.5;

export const BOOTSTRAP_CONTEXT: EvalContext = {
  referenceBandId: CANONICAL_GENERATION_EVALUATION_ANCHOR.band,
  // Scalar Poisson intensity ρ (monsters / m²), assuming a uniform spatial
  // spread: a skill is aimed away from the player, so the crowd it lands in is
  // not centred on the player and a player-centred distribution would be the
  // wrong model for pricing it.
  //
  // PROVISIONAL. This value was originally back-solved to make one worked
  // example land on E[N]≈1.98 at r=3m, not measured; it is the one number in
  // the E[N] formula (`eval/targeting.ts`) still owed a real measurement, and
  // every area skill's price moves proportionally with it. Issue #105.
  enemyDensity: 0.03466,
  castField: {
    reachEdgeShare: 0.17,
    engageShare: 0.3,
    engageRangeM: 12,
    firstOpportunityMarginM: 0.25,
    bandDensityOverField: 1.73,
    bandHalfWidthM: 5,
    frontLengthM: 4,
  },
  // PROVISIONAL: the reference target's (`sim/cc-resistance.ts`), the one the runtime's own resistance is evaluated at.
  ccSusceptibility: CC_PRICING_SUSCEPTIBILITY,
  displacementSusceptibility: DISPLACEMENT_PRICING_SUSCEPTIBILITY,
  healUtilization: 0.75,
  shieldUtilization: 0.65,
  expectedHealRate: 1.2,
  relevantDps: 5,
  incomingDps: 5,
  // Cast and recovery time must earn back the basic attacks they displace in a mixed rotation.
  displacedBasic: resolveDisplacedBasicSource(false, CANONICAL_GENERATION_EVALUATION_ANCHOR.level),
  // Bootstrap from the mixed+swarm kiting paired probe. Refit per encounter
  // family once production telemetry replaces the synthetic simulator.
  castExposureValuePerLockedS: 9.4,
  positionalPotentialPerM: 0.5,
  expectedHarmfulStatuses: 0.6,
  harmfulStatusValue: 3,
  expectedCooldownRemainingFraction: 0.5,
  fallbackReferencedSkillValue: 5,
  fallbackReferencedCooldownS: 6,
  selfHpFraction: 0.8,
  conditionProb: (p) => defaultConditionProb(p),
  triggerProb: {
    // The runtime fires `on_hit` on every hit, so given the hit the trigger fires with certainty.
    on_hit: 1,
    // PROVISIONAL: the reference dummy never dies, so runtime delivers 0; this placeholder basis is not runtime parity.
    on_kill: 0.2,
    on_apply: 1,
    on_expire: 0.8,
    // PROVISIONAL: the reference dummy never collides, so runtime delivers 0; this placeholder basis is not runtime parity.
    on_terrain_collision: 0.25,
    // `on_projectile_expire` deliberately has no entry. It is not an event
    // with a rate of its own — it is the miss side of its own delivery, so
    // `projectileExpireActivation` (`eval/hitprob.ts`) derives it from that
    // delivery's landing probability rather than reading a constant here.
  },
};

function defaultConditionProb(p: Predicate): number {
  if (p.and) return p.and.reduce((acc, c) => acc * defaultConditionProb(c), 1);
  if (p.or) {
    // P(A ∨ B) = 1 - Π(1 - P)
    return 1 - p.or.reduce((acc, c) => acc * (1 - defaultConditionProb(c)), 1);
  }
  if (p.not) return 1 - defaultConditionProb(p.not);
  if (p.hasTag !== undefined) return DEFAULT_CONDITION_PROB[`tag:${p.hasTag}`] ?? UNLISTED_TAG_CONDITION_PROB_PROVISIONAL;
  if (p.hpBelow !== undefined) return Math.min(0.9, p.hpBelow); // crude: low-HP fraction ~ threshold
  return UNRECOGNISED_PREDICATE_CONDITION_PROB_PROVISIONAL;
}

/** Shallow-clone the bootstrap context with overrides (handy for tests/tuning). */
export function makeContext(overrides: Partial<EvalContext> = {}): EvalContext {
  return { ...BOOTSTRAP_CONTEXT, ...overrides };
}
